import { Link } from 'react-router-dom';
import { useEffect } from 'react';

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-sm border-b border-yellow-500/20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <i className="ri-arrow-left-line text-xl text-yellow-500"></i>
            <span className="text-lg font-semibold">Back to Home</span>
          </Link>
          <div className="text-yellow-500 font-bold text-xl">Amagi Poker</div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-24 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-yellow-500 to-yellow-300 bg-clip-text text-transparent">
              Privacy Policy
            </h1>
            <p className="text-gray-400">Last Updated: January 2026</p>
          </div>

          {/* Content Sections */}
          <div className="space-y-8">
            {/* Introduction */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">1. Introduction</h2>
              <p className="text-gray-300 leading-relaxed">
                Welcome to Amagi Poker. We respect your privacy and are committed to protecting your personal data. This privacy policy will inform you about how we look after your personal data when you visit our application and tell you about your privacy rights and how the law protects you.
              </p>
            </section>

            {/* Data Collection */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">2. Data We Collect</h2>
              <div className="space-y-4 text-gray-300">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">2.1 Personal Information</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Account registration information (username, email address, phone number)</li>
                    <li>Profile information (avatar, display name, preferences)</li>
                    <li>Authentication data (login credentials, security tokens)</li>
                    <li>Payment information (transaction history, payment methods)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">2.2 Usage Data</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Game statistics and performance data</li>
                    <li>In-app activity and interaction patterns</li>
                    <li>Device information (device type, operating system, unique identifiers)</li>
                    <li>Log data (IP address, access times, app crashes)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">2.3 Communication Data</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Customer support correspondence</li>
                    <li>In-game chat messages and communications</li>
                    <li>Feedback and survey responses</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* How We Use Data */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">3. How We Use Your Data</h2>
              <div className="space-y-3 text-gray-300">
                <p className="leading-relaxed">We use your personal data for the following purposes:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-white">Service Provision:</strong> To provide, maintain, and improve our gaming services</li>
                  <li><strong className="text-white">Account Management:</strong> To create and manage your user account</li>
                  <li><strong className="text-white">Game Experience:</strong> To personalize your gaming experience and save your progress</li>
                  <li><strong className="text-white">Communication:</strong> To send you updates, notifications, and promotional materials</li>
                  <li><strong className="text-white">Security:</strong> To detect and prevent fraud, abuse, and security incidents</li>
                  <li><strong className="text-white">Analytics:</strong> To analyze usage patterns and improve our services</li>
                  <li><strong className="text-white">Legal Compliance:</strong> To comply with legal obligations and enforce our terms</li>
                </ul>
              </div>
            </section>

            {/* Data Sharing */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">4. Data Sharing and Third Parties</h2>
              <div className="space-y-4 text-gray-300">
                <p className="leading-relaxed">We may share your personal data with:</p>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">4.1 Service Providers</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Cloud hosting and storage providers</li>
                    <li>Payment processing services</li>
                    <li>Analytics and data analysis platforms</li>
                    <li>Customer support tools</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">4.2 Legal Requirements</h3>
                  <p className="ml-4">We may disclose your information when required by law, court order, or government regulation, or to protect our rights and safety.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">4.3 Business Transfers</h3>
                  <p className="ml-4">In the event of a merger, acquisition, or sale of assets, your data may be transferred to the acquiring entity.</p>
                </div>
              </div>
            </section>

            {/* Cookies */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">5. Cookies and Tracking Technologies</h2>
              <div className="space-y-3 text-gray-300">
                <p className="leading-relaxed">We use cookies and similar tracking technologies to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Remember your preferences and settings</li>
                  <li>Authenticate your login sessions</li>
                  <li>Analyze app performance and user behavior</li>
                  <li>Deliver personalized content and advertisements</li>
                </ul>
                <p className="leading-relaxed mt-4">You can control cookie settings through your device settings, but disabling cookies may affect app functionality.</p>
              </div>
            </section>

            {/* Data Security */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">6. Data Security</h2>
              <p className="text-gray-300 leading-relaxed">
                We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. These measures include encryption, secure servers, access controls, and regular security assessments. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            {/* Data Retention */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">7. Data Retention</h2>
              <p className="text-gray-300 leading-relaxed">
                We retain your personal data only for as long as necessary to fulfill the purposes outlined in this privacy policy, unless a longer retention period is required by law. When data is no longer needed, we will securely delete or anonymize it. Account data is typically retained for the duration of your account plus a reasonable period thereafter for legal and business purposes.
              </p>
            </section>

            {/* Your Rights */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">8. Your Privacy Rights</h2>
              <div className="space-y-3 text-gray-300">
                <p className="leading-relaxed">You have the following rights regarding your personal data:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-white">Access:</strong> Request access to your personal data</li>
                  <li><strong className="text-white">Correction:</strong> Request correction of inaccurate or incomplete data</li>
                  <li><strong className="text-white">Deletion:</strong> Request deletion of your personal data</li>
                  <li><strong className="text-white">Portability:</strong> Request transfer of your data to another service</li>
                  <li><strong className="text-white">Objection:</strong> Object to processing of your data for certain purposes</li>
                  <li><strong className="text-white">Restriction:</strong> Request restriction of processing in certain circumstances</li>
                  <li><strong className="text-white">Withdraw Consent:</strong> Withdraw consent for data processing at any time</li>
                </ul>
                <p className="leading-relaxed mt-4">To exercise these rights, please contact us using the information provided below.</p>
              </div>
            </section>

            {/* Children's Privacy */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">9. Children's Privacy</h2>
              <p className="text-gray-300 leading-relaxed">
                Our services are not intended for children under the age of 18. We do not knowingly collect personal data from children under 18. If you are a parent or guardian and believe your child has provided us with personal data, please contact us immediately, and we will take steps to delete such information.
              </p>
            </section>

            {/* International Transfers */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">10. International Data Transfers</h2>
              <p className="text-gray-300 leading-relaxed">
                Your personal data may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws. We ensure that appropriate safeguards are in place to protect your data in accordance with this privacy policy and applicable laws.
              </p>
            </section>

            {/* Changes to Policy */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">11. Changes to This Privacy Policy</h2>
              <p className="text-gray-300 leading-relaxed">
                We may update this privacy policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by posting the new privacy policy on this page and updating the "Last Updated" date. We encourage you to review this privacy policy periodically.
              </p>
            </section>
          </div>

          {/* Back to Top */}
          <div className="mt-12 text-center">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex items-center gap-2 px-6 py-3 bg-yellow-500 text-black font-semibold rounded-full hover:bg-yellow-400 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-up-line"></i>
              Back to Top
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
